export const DEFAULT = "@@default";

export const createReducer = (...args) => {
    const [initialState, handlers] = args.length === 2 ? args : [null, args[0]];
    return (state = initialState, action) => {
        if (action.type in handlers) {
            return handlers[action.type](state, action);
        }
        if (DEFAULT in handlers) {
            return handlers[DEFAULT](state, action);
        }
        return state;
    };
};

const apiInitialState = {
    loaded: false,
    loading: false,
    error: false,
};

export const createApi = (name = "", state = { data: [] }) => {
    if (typeof name === "string") {
        const prefix = name;
        name = p => `${prefix}_${p}`;
    }

    const initialState = { ...apiInitialState, ...state };

    const result = {
        name,
        request: name("request"),
        success: name("success"),
        error: name("error"),
    };

    result.handlers = {
        [result.request]: (state, { type, ...action }) => ({
            ...state,
            ...action,
            loading: true,
        }),
        [result.success]: (state, { type, ...action }) => ({
            ...state,
            ...action,
            loading: false,
            loaded: true,
            error: false,
        }),
        [result.error]: (state, { error }) => ({
            ...state,
            loading: false,
            loaded: false,
            error,
        }),
    };

    result.reducer = createReducer(initialState, result["handlers"]);

    // api to override some handlers
    result.setHandler = (name, fn, extend = true) => {
        if (!extend) {
            result.handlers[name] = fn;
        } else {
            const orig = result.handlers[name];
            result.handlers[name] = (state, action) => fn(orig ? orig(state, action) : state, action);
        }
    };

    return result;
};

function getPath(path, of, sep = "/") {
    if (!(typeof path === "string")) throw new Error("getPath: Path must be set");
    const vs = path.split(sep);

    of || (of = {});

    for (var i = 0; i < vs.length; i++) {
        if (typeof of[vs[i]] == "undefined") of[vs[i]] = {};
        of = of[vs[i]];
    }

    return of;
}

export const factory = ({
    /**
     * the namespace. eg. "my/perfect/namespace"
     * @type {String}
     */
    root = "",

    /**
     * ensures that a object in the map (by id) is missing
     * will be created and initialised with itemInitialData
     * @type {Boolean}
     */
    ensure = false,

    /**
     * initial data for a single item in the map
     * @type {Object}
     */
    itemInitialData = {},
}) => {
    // given a path (ns/ns/ns) selects the corresponding
    // object in the state tree
    const selectRoot = state => getPath(root, state);

    // returns a function that receives correc root in the state for this namespace
    const withRoot =
        fn =>
        (state, ...args) =>
            fn.apply(null, [selectRoot(state)].concat(args));

    // returns the full name (prefixes the namespace)
    const getName = name => `${root}/${name}`;

    const select = mapSelector(ensure && itemInitialData);

    return {
        getName,
        selectRoot,
        withRoot,
        select,
        update: mapUpdater(select, itemInitialData),
        reset: mapResetter(itemInitialData),
        remove: mapRemove(),
    };
};

export function mapSelector(createInitial) {
    return (state, key) => {
        let value = state[key];
        if (typeof value === "undefined" && !!createInitial) {
            value = state[key] = { ...createInitial };
        }
        return value;
    };
}

export function mapUpdater(select, initial) {
    return (state, id, data) => ({
        ...state,
        [id]: {
            ...initial,
            ...select(state, id),
            ...data,
        },
    });
}

export function mapResetter(initial) {
    return (state, id, data) => ({
        ...state,
        [id]: {
            ...initial,
            ...data,
        },
    });
}

export function mapRemove() {
    return (state, id) => {
        if (state[id]) {
            delete state[id];
        }
        return { ...state };
    };
}
